import { useCallback, useRef } from "react";
import Flex from "@/components/Flex";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import useDisclosure from "@/hooks/use-disclosure";
import GroupForm from "./components/GroupForm";
import {
  GROUP_ACTION_LABEL,
  GROUP_CONFIRMATION,
  GROUP_CREATE_SECTIONS,
  GROUP_DRAWER_WIDTH,
  GROUP_FORM_MODE,
  GROUP_SUBMIT_ACTION,
  GROUP_TITLE,
} from "./group-visit-constant";
import {
  GroupDetail,
  GroupFormMode,
  GroupPayload,
  GroupSubmitAction,
} from "./group-visit-types";
import { useGroupForm } from "./hooks/use-group-form";

export type GroupFormDrawerProps = {
  opened: boolean;
  onClose: () => void;
  mode?: GroupFormMode;
  /** Saved group for edit mode; omit to create. */
  group?: GroupDetail;
  existingGroupNames?: string[];
  /** Resolve to keep the drawer open on failure — it closes only on success. */
  onSubmit: (
    payload: GroupPayload,
    action: GroupSubmitAction
  ) => Promise<unknown> | void;
  /** View-only users get a read-only form with no save action. */
  canEdit?: boolean;
};

/**
 * @author Rajkumar Rathod
 */
const GroupFormDrawer = ({
  opened,
  onClose,
  mode = GROUP_FORM_MODE.CREATE,
  group,
  existingGroupNames,
  onSubmit,
  canEdit = true,
}: GroupFormDrawerProps) => {
  const discardDisclosure = useDisclosure();
  const submitActionRef = useRef<GroupSubmitAction>(GROUP_SUBMIT_ACTION.SAVE);

  const { formik, isEditMode, isArchived, resetToSaved } = useGroupForm({
    mode,
    sections: GROUP_CREATE_SECTIONS,
    group,
    existingGroupNames,
    onSubmit: async (payload) => {
      await onSubmit(payload, submitActionRef.current);
      closeAndReset();
    },
  });

  const isReadOnly = !canEdit || isArchived;

  const closeAndReset = useCallback(() => {
    resetToSaved();
    onClose();
  }, [resetToSaved, onClose]);

  const handleCancel = () => {
    if (formik.dirty && !isReadOnly) {
      discardDisclosure.open();
      return;
    }
    closeAndReset();
  };

  const submitWith = (action: GroupSubmitAction) => {
    submitActionRef.current = action;
    formik.handleSubmit();
  };

  const isSubmitDisabled =
    formik.isSubmitting || !formik.dirty || !formik.isValid;

  const handleDiscard = () => {
    discardDisclosure.close();
    closeAndReset();
  };

  return (
    <SideSheetDrawer
      open={opened}
      title={
        isEditMode && group?.groupName
          ? `${GROUP_TITLE[GROUP_FORM_MODE.EDIT]} - ${group.groupName}`
          : GROUP_TITLE[mode]
      }
      onClose={handleCancel}
      w={GROUP_DRAWER_WIDTH}
      endIcon={
        !isReadOnly && (
          <Flex gap={1}>
            <StyledButtonV2
              label={
                isEditMode ? GROUP_ACTION_LABEL.save : GROUP_ACTION_LABEL.create
              }
              variant={isEditMode ? "primary" : "outlined"}
              disabled={isSubmitDisabled}
              onClick={() => submitWith(GROUP_SUBMIT_ACTION.SAVE)}
            />
            {!isEditMode && (
              <StyledButtonV2
                label={GROUP_ACTION_LABEL.saveAndNext}
                variant="primary"
                disabled={isSubmitDisabled}
                onClick={() => submitWith(GROUP_SUBMIT_ACTION.SAVE_AND_NEXT)}
              />
            )}
          </Flex>
        )
      }
    >
      <GroupForm
        formik={formik}
        sections={GROUP_CREATE_SECTIONS}
        disabled={isReadOnly}
        showAmount={false}
      />

      <ConfirmationModal
        disclosureHost={discardDisclosure}
        title={GROUP_CONFIRMATION.discardTitle}
        content={GROUP_CONFIRMATION.discardContent}
        confirmationText={GROUP_CONFIRMATION.discardConfirm}
        onConfirm={handleDiscard}
      />
    </SideSheetDrawer>
  );
};

export default GroupFormDrawer;
